import { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import AuthLanguageSwitcher from '@/components/AuthLanguageSwitcher.jsx';
import CodeInput from '@/components/CodeInput.jsx';
import {
  DEFAULT_LANGUAGE,
  detectLanguageFromPath,
  getStoredLanguage,
  resolveLanguagePriority,
} from '@/utils/languageRouting.js';
import { isPasswordValid, MIN_PASSWORD_LENGTH } from '@/utils/passwordValidation.js';

const normalizeResetLocale = (language) => (language === 'ua' ? 'uk' : language);
const isValidEmail = (email) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(email);

const PasswordResetPage = () => {
  const { t, i18n } = useTranslation('dashboard');
  const { getLocalizedPath, language } = useLanguageNavigation();
  const location = useLocation();
  const routerNavigate = useNavigate();
  const [step, setStep] = useState('request');
  const [form, setForm] = useState({
    email: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [codeDigits, setCodeDigits] = useState(() => Array(6).fill(''));
  const [resetToken, setResetToken] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const storedLanguage = useMemo(() => getStoredLanguage(), []);
  const resolveAuthLanguage = useCallback(
    () =>
      resolveLanguagePriority(
        detectLanguageFromPath(location.pathname),
        language,
        storedLanguage,
        i18n.language,
        DEFAULT_LANGUAGE,
      ),
    [i18n.language, language, location.pathname, storedLanguage],
  );
  const loginLink = useMemo(() => getLocalizedPath('/login'), [getLocalizedPath]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const parseErrorCode = async (response) => {
    try {
      const data = await response.json();
      return data?.error || null;
    } catch (err) {
      return null;
    }
  };

  const formatNetworkError = (networkError) => {
    if (networkError?.name === 'TypeError' || networkError?.message === 'Failed to fetch') {
      return t('auth.passwordReset.errors.network');
    }
    return networkError?.message || t('auth.passwordReset.errors.generic');
  };

  const handleRequestSubmit = async (event) => {
    event.preventDefault();
    const trimmedEmail = form.email.trim();
    if (!trimmedEmail) {
      setError(t('auth.passwordReset.request.errors.required'));
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError(t('auth.passwordReset.request.errors.invalid'));
      return;
    }
    setPending(true);
    setError('');
    try {
      const response = await fetch(apiUrl('/api/auth/password-reset/request'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          locale: normalizeResetLocale(resolveAuthLanguage()),
        }),
      });

      if (!response.ok) {
        const message = await parseErrorCode(response);
        if (message === 'USER_NOT_FOUND') {
          setError(t('auth.passwordReset.request.errors.notFound'));
          return;
        }
        throw new Error(message || t('auth.passwordReset.errors.requestFailed'));
      }

      setStep('code');
      setForm((prev) => ({ ...prev, email: trimmedEmail }));
      setCodeDigits(Array(6).fill(''));
    } catch (err) {
      setError(formatNetworkError(err));
    } finally {
      setPending(false);
    }
  };

  const handleVerifySubmit = async (event) => {
    event.preventDefault();
    const code = codeDigits.join('').trim();
    if (code.length !== 6) {
      setError(t('auth.passwordReset.code.error'));
      return;
    }
    setPending(true);
    setError('');
    try {
      const response = await fetch(apiUrl('/api/auth/password-reset/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, code }),
      });

      if (!response.ok) {
        const code = await parseErrorCode(response);
        if (code === 'INVALID_CODE') {
          setError(t('auth.passwordReset.code.error'));
          return;
        }
        throw new Error(t('auth.passwordReset.errors.requestFailed'));
      }

      const data = await response.json();
      setResetToken(data?.resetToken || '');
      setStep('confirm');
      setForm((prev) => ({ ...prev, newPassword: '', confirmPassword: '' }));
    } catch (err) {
      setError(formatNetworkError(err));
    } finally {
      setPending(false);
    }
  };

  const handleConfirmSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (form.newPassword !== form.confirmPassword) {
      setError(t('auth.passwordReset.confirm.mismatch'));
      return;
    }

    if (!isPasswordValid(form.newPassword)) {
      setError(t('security.messages.length', { length: MIN_PASSWORD_LENGTH }));
      return;
    }

    setPending(true);
    try {
      const response = await fetch(apiUrl('/api/auth/password-reset/confirm'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resetToken, newPassword: form.newPassword }),
      });

      if (!response.ok) {
        const code = await parseErrorCode(response);
        if (code === 'INVALID_TOKEN') {
          setError(t('auth.passwordReset.confirm.invalidToken'));
          return;
        }
        throw new Error(t('auth.passwordReset.errors.requestFailed'));
      }

      routerNavigate(`${loginLink}?reset=success`, { replace: true });
    } catch (err) {
      setError(formatNetworkError(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="auth-page">
      <form
        className="auth-card"
        onSubmit={
          step === 'request' ? handleRequestSubmit : step === 'code' ? handleVerifySubmit : handleConfirmSubmit
        }
      >
        <AuthLanguageSwitcher />
        {step === 'request' && (
          <>
            <h1>{t('auth.passwordReset.request.title')}</h1>
            <label>
              {t('auth.passwordReset.request.email')}
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </label>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" disabled={pending}>
              {t('auth.passwordReset.request.submit')}
            </button>
            <p className="auth-helper">{t('auth.passwordReset.request.hint')}</p>
            <p className="auth-helper">
              <Link to={loginLink}>{t('auth.register.login')}</Link>
            </p>
          </>
        )}

        {step === 'code' && (
          <>
            <h1>{t('auth.passwordReset.code.title')}</h1>
            <div className="code-section">
              <div className="code-step">
                <div className="code-head">
                  <h2 className="code-title">{t('auth.passwordReset.code.label')}</h2>
                  <p className="code-subtitle">{t('auth.passwordReset.code.hint')}</p>
                </div>
                <CodeInput
                  digits={codeDigits}
                  onChange={setCodeDigits}
                  autoFocus={step === 'code'}
                  getAriaLabel={(index) => t('auth.register.code.ariaLabel', { index: index + 1 })}
                />
              </div>
            </div>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" disabled={pending}>
              {t('auth.passwordReset.code.submit')}
            </button>
            <p className="auth-helper">
              <Link to={loginLink}>{t('auth.register.login')}</Link>
            </p>
          </>
        )}

        {step === 'confirm' && (
          <>
            <h1>{t('auth.passwordReset.confirm.title')}</h1>
            <label>
              {t('auth.passwordReset.confirm.newPassword')}
              <input
                name="newPassword"
                type="password"
                value={form.newPassword}
                onChange={handleChange}
                required
              />
            </label>
            <label>
              {t('auth.passwordReset.confirm.confirmPassword')}
              <input
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                required
              />
            </label>
            {error && <p className="auth-error">{error}</p>}
            <button type="submit" disabled={pending}>
              {t('auth.passwordReset.confirm.submit')}
            </button>
            <p className="auth-helper">
              <Link to={loginLink}>{t('auth.register.login')}</Link>
            </p>
          </>
        )}
      </form>
    </div>
  );
};

export default PasswordResetPage;
